import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserStore } from "../../../store/user.store";
import { ImageUploader } from "../../components/ui/image-uploader.ui";
import Input from "../../components/core/input.core";
import Button from "../../components/core/button.core";
import { useToast } from "../../../hooks/use-toast";
import { useUpdateProfileHttp } from "../../hooks/http/users/update-profile.hook";
import { handleHttpError } from "../../../utils/utils";
import LogoutIcon from "../../../assets/svgs/common/logout.svg";

export default function ProfileTabPartial() {
  // Fix the profileSchema to make coverImageFile optional
  const profileSchema = z.object({
    coverImageFile: z.array(z.instanceof(File)).optional(),
    coverImage: z
      .object({ id: z.string(), url: z.string() })
      .nullable()
      .default(null)
      .optional(),
    fullName: z.string().min(3, { message: "Full name is required" }),
    email: z.string().email().optional(),
  });

  type ProfileFormValues = z.infer<typeof profileSchema>;

  const { user, brands, logout } = useUserStore();

  const { toast } = useToast();
  const { execute: updateProfileHttp, fetching: updatingProfile } =
    useUpdateProfileHttp();
  const {
    control,
    getValues,
    reset,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema), // Changed from FormSchema to profileSchema
    defaultValues: {
      coverImage: brands?.[0].logo,
      coverImageFile: [],
      fullName: user?.name,
      email: user?.email,
    },
  });
  const onSubmit = async (data: ProfileFormValues) => {
    console.log(data);
    const response = await updateProfileHttp({
      body: {
        name: data.fullName,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "Profile updated successfully",
      toastType: "success",
    });
    reset();
  };

  return (
    <div className="relative flex flex-col justify-center items-center w-full h-full font-cabinetgrotesk">
      <div className="flex items-start p-5 pt-10 w-full h-full">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="gap-10 grid grid-cols-2 w-full"
        >
          <div className="flex items-center gap-5 col-span-2 w-full h-fit">
            <Controller
              control={control}
              disabled
              name="coverImageFile"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1">
                  {getValues("coverImage")?.url ? (
                    <img
                      src={getValues("coverImage")?.url}
                      alt="profile"
                      className="rounded-xl w-24 h-24 object-cover"
                    />
                  ) : (
                    <ImageUploader
                      label=""
                      onChange={(files: File[]) => field.onChange(files)}
                      value={field.value}
                      className="flex justify-center items-center border-black w-24 h-24"
                    />
                  )}
                  {fieldState.error && (
                    <p className="text-red-500 text-sm">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
            <div className="flex flex-col">
              <p className="font-normal text-2xl">Display picture</p>
            </div>
          </div>
          <div className="flex md:flex-row flex-col items-center gap-10 col-span-2 w-full h-fit">
            <Controller
              name="fullName"
              disabled
              control={control}
              render={({ field }) => (
                <Input
                  label="Full name"
                  placeholder="Enter your full name"
                  error={errors.fullName?.message}
                  variant="default"
                  fullWidth
                  {...field}
                />
              )}
            />
            <Controller
              name="email"
              disabled
              control={control}
              render={({ field }) => (
                <Input
                  label="Work email"
                  placeholder="Enter your work email"
                  error={errors.email?.message}
                  variant="default"
                  fullWidth
                  {...field}
                />
              )}
            />
          </div>
        </form>
      </div>
      <div className="right-0 bottom-0 sticky flex justify-end items-center gap-5 bg-white px-5 pt-4 border-t border-black w-full">
        <div className="md:flex hidden">
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
          <Button
            variant="secondary"
            onClick={() => logout()}
            className="flex gap-2 px-5 w-full h-14 font-semibold text-rejectbuttontext text-sm cursor-pointer"
          >
            <LogoutIcon />
            Logout
          </Button>
        </div>
        <div className="w-full md:w-40">
          <Button
            className="flex justify-center items-center px-6 w-full h-14 font-semibold text-sm"
            disabled={!isDirty}
            isLoading={updatingProfile}
            variant="secondary"
            onClick={handleSubmit(onSubmit)}
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
